import React, { useState, useEffect } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const EMPTY_DATA = {
  Bonpas: 0,
  DaNang: 0,
  DongTien: 0,
  BieuTang: 0,
  KhoQC: 0,
  ThanhLyNhap: 0,
  ThanhLyBan: 0,
  BanhAnThu: 0,
  CEO: 0,
  CSKH: 0,
};

const sumNonBoxQuantity = (payload, field) => {
  const rows = Object.values(payload?.dataByStore || {}).flat();
  if (rows.length === 0) return null;

  return rows.reduce((sum, item) => {
    const cakeType = String(item?.MaLBTT || '').trim().toUpperCase();
    return cakeType === 'HOP' ? sum : sum + (Number(item?.[field]) || 0);
  }, 0);
};

const columnTotalLabelsPlugin = {
  id: 'columnTotalLabels',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    const xScale = scales.x;
    const yScale = scales.y;

    ctx.save();
    ctx.fillStyle = '#374151';
    ctx.font = '600 11px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';

    data.labels.forEach((_, index) => {
      const total = data.datasets.reduce(
        (sum, dataset) =>
          dataset.isVirtualHeight
            ? sum
            : sum + (Number(dataset.data[index]) || 0),
        0
      );

      if (total > 0) {
        ctx.fillText(
          String(total),
          xScale.getPixelForTick(index),
          yScale.getPixelForValue(total) - 4
        );
      }
    });

    ctx.restore();
  },
};

const BT_BDNB = ({
  selectedLocation,
  ngayBatDau,
  ngayKetThuc,
  onDataChange,
}) => {
  const [data, setData] = useState({ ...EMPTY_DATA });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const fetchData = async () => {
      setLoading(true);

      if (!ngayBatDau || !ngayKetThuc || ngayBatDau > ngayKetThuc) {
        setData({ ...EMPTY_DATA });
        onDataChange?.({ ...EMPTY_DATA });
        setLoading(false);
        return;
      }

      const fetchJson = async (path, extraQuery = {}) => {
        const query = new URLSearchParams({
          NgayBatDau: ngayBatDau,
          NgayKetThuc: ngayKetThuc,
          ...extraQuery,
        }).toString();

        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}${path}?${query}`,
            { signal: controller.signal }
          );

          if (!response.ok) {
            throw new Error(`${path} failed with status ${response.status}`);
          }

          return await response.json();
        } catch (error) {
          if (error.name === 'AbortError') {
            throw error;
          }

          console.error(`Error fetching ${path}:`, error);
          return {};
        }
      };

      try {
        const [
          daNangData,
          bonpasData,
          bieuTangData,
          khoQCData,
          ceoCskhData,
          thanhLyNhapData,
          thanhLyBanData,
          banhAnThuData,
        ] = await Promise.all([
          fetchJson('/api/DonHangPBBP/DaNang'),
          fetchJson('/api/DonHangPBBP/Bonpas'),
          fetchJson('/api/DonHangBieuTang'),
          fetchJson('/api/NhapKhoQC'),
          fetchJson('/api/BTT-CEO-CSKH'),
          fetchJson('/api/btt-ledger/imports', { MaCH: 666 }),
          fetchJson('/api/btt-ledger/sales', { MaCH: 666 }),
          fetchJson('/api/btt-ledger/imports', { MaCH: 6969 }),
        ]);

        if (controller.signal.aborted) {
          return;
        }

        const thanhLyNhapWithoutBoxes = sumNonBoxQuantity(
          thanhLyNhapData,
          'SoLuongNhap'
        );
        const thanhLyBanWithoutBoxes = sumNonBoxQuantity(
          thanhLyBanData,
          'SoLuongBan'
        );
        const banhAnThuWithoutBoxes = sumNonBoxQuantity(
          banhAnThuData,
          'SoLuongNhap'
        );

        const nextData = {
          DongTien: Number(daNangData.DaNang) || 0,
          Bonpas: Number(bonpasData.Bonpas) || 0,
          DaNang:
            (Number(daNangData.DaNang) || 0) +
            (Number(bonpasData.Bonpas) || 0),
          BieuTang: Number(bieuTangData.BieuTang) || 0,
          KhoQC: Number(khoQCData.KhoQC) || 0,
          ThanhLyNhap: Number(
            thanhLyNhapWithoutBoxes ??
              thanhLyNhapData.summaryByStore?.['666']?.totalSoLuongNhap ??
              thanhLyNhapData.grandSummary?.grandTotalSoLuongNhap
          ) || 0,
          ThanhLyBan: Number(
            thanhLyBanWithoutBoxes ??
              thanhLyBanData.summaryByStore?.['666']?.totalByType?.BanRa
                ?.totalSoLuong ??
              thanhLyBanData.grandSummary?.grandTotal?.BanRa?.totalSoLuong
          ) || 0,
          BanhAnThu: Number(
            banhAnThuWithoutBoxes ??
              banhAnThuData.summaryByStore?.['6969']?.totalSoLuongNhap ??
              banhAnThuData.grandSummary?.grandTotalSoLuongNhap
          ) || 0,
          CEO: Number(ceoCskhData.CEO) || 0,
          CSKH: Number(ceoCskhData.CSKH) || 0,
        };
        setData(nextData);
        onDataChange?.(nextData);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error fetching chart data:', error);
          setData({ ...EMPTY_DATA });
          onDataChange?.({ ...EMPTY_DATA });
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      controller.abort();
    };
  }, [selectedLocation, ngayBatDau, ngayKetThuc, onDataChange]);

  const chartItems = [
    { label: 'Bánh biếu tặng', value: data.BieuTang, color: '#10b981' },
    { label: 'Trả hủy, lưu mẫu', value: data.KhoQC, color: '#ef4444' },
    { label: 'Bánh thanh lý', type: 'thanhLy' },
    { label: 'Bánh ăn thử', value: data.BanhAnThu, color: '#8b5cf6' },
  ];

  // Tạm ẩn cột Bánh đặt Bonpas.
  // if (selectedLocation !== 'hcm' && selectedLocation !== 'danang') {
  //   chartItems.push({ label: 'Bánh đặt Bonpas', value: data.Bonpas, color: '#3b82f6' });
  // }

  if (selectedLocation !== 'hcm' && selectedLocation !== 'bonpas') {
    chartItems.push({
      label: 'Khách hàng công ty (Bán tại T7)',
      type: 'khachHangCongTy',
    });
  }

  // Tạm ẩn các cột CEO và CSKH.
  // chartItems.push(
  //   { label: 'CEO', value: data.CEO, color: '#ef4444' },
  //   { label: 'CSKH', value: data.CSKH, color: '#8b5cf6' }
  // );

  const quantityDatasets = [
      {
        label: 'Số lượng',
        data: chartItems.map((item) => item.type ? null : item.value),
        backgroundColor: chartItems.map((item) => item.color || 'transparent'),
        borderColor: chartItems.map((item) => item.color || 'transparent'),
        borderWidth: 1,
        stack: 'quantity',
      },
      {
        label: 'Đã bán',
        data: chartItems.map((item) => item.type === 'thanhLy' ? data.ThanhLyBan : null),
        backgroundColor: '#10b981',
        borderColor: '#10b981',
        borderWidth: 1,
        stack: 'quantity',
      },
      {
        label: 'Chưa bán',
        data: chartItems.map((item) =>
          item.type === 'thanhLy' ? data.ThanhLyNhap - data.ThanhLyBan : null
        ),
        backgroundColor: '#ec4899',
        borderColor: '#ec4899',
        borderWidth: 1,
        stack: 'quantity',
      },
      {
        label: 'Bánh Bonpas',
        data: chartItems.map((item) =>
          item.type === 'khachHangCongTy' ? data.Bonpas : null
        ),
        backgroundColor: '#3b82f6',
        borderColor: '#3b82f6',
        borderWidth: 1,
        stack: 'quantity',
      },
      {
        label: 'Bánh Đồng Tiến',
        data: chartItems.map((item) =>
          item.type === 'khachHangCongTy' ? data.DongTien : null
        ),
        backgroundColor: '#fbbf24',
        borderColor: '#fbbf24',
        borderWidth: 1,
        stack: 'quantity',
      },
  ];
  const columnTotals = chartItems.map((_, index) =>
    quantityDatasets.reduce(
      (sum, dataset) => sum + (Number(dataset.data[index]) || 0),
      0
    )
  );
  const maximumColumnTotal = Math.max(0, ...columnTotals);

  const chartData = {
    labels: chartItems.map((item) => item.label),
    datasets: [
      ...quantityDatasets,
      {
        label: 'Chiều cao ảo',
        data: columnTotals.map((total) =>
          total > 0 && total < maximumColumnTotal
            ? maximumColumnTotal - total
            : null
        ),
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderWidth: 0,
        isVirtualHeight: true,
        stack: 'quantity',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        stacked: true,
        ticks: { display: false },
      },
      y: { stacked: true },
    },
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          boxWidth: 10,
          boxHeight: 10,
          padding: 6,
          font: { size: 10 },
          generateLabels: () => [
            {
              text: 'Biếu tặng',
              fillStyle: '#10b981',
              strokeStyle: '#10b981',
              datasetIndex: 0,
            },
            {
              text: 'Trả hủy',
              fillStyle: '#ef4444',
              strokeStyle: '#ef4444',
              datasetIndex: 0,
            },
            {
              text: 'TL đã bán',
              fillStyle: '#10b981',
              strokeStyle: '#10b981',
              datasetIndex: 1,
            },
            {
              text: 'TL chưa bán',
              fillStyle: '#ec4899',
              strokeStyle: '#ec4899',
              datasetIndex: 2,
            },
            {
              text: 'Bonpas',
              fillStyle: '#3b82f6',
              strokeStyle: '#3b82f6',
              datasetIndex: 3,
            },
            {
              text: 'Đồng Tiến',
              fillStyle: '#fbbf24',
              strokeStyle: '#fbbf24',
              datasetIndex: 4,
            },
          ],
        },
      },
      tooltip: {
        filter: (context) =>
          !context.dataset.isVirtualHeight && Number(context.parsed.y) > 0,
        callbacks: {
          label: (context) => {
            if (
              context.label === 'Khách hàng công ty (Bán tại T7)' &&
              ['Bánh Bonpas', 'Bánh Đồng Tiến'].includes(context.dataset.label)
            ) {
              return `${context.dataset.label}: ${context.parsed.y} / ${data.DaNang}`;
            }

            return `${context.dataset.label}: ${context.parsed.y}`;
          },
        },
      },
      title: {
        display: true,
        text: `Biểu đồ số lượng khác (${ngayBatDau} - ${ngayKetThuc})`,
      },
    },
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <div className="loading-text">Đang tải dữ liệu...</div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <h3>Số lượng khác</h3>
      <div className="chart-wrapper">
        <Bar
          data={chartData}
          options={options}
          plugins={[columnTotalLabelsPlugin]}
        />
      </div>
    </div>
  );
};

export default BT_BDNB;
